/**
 * The criteria contract: one row per backend modifier, carrying both what the drawer
 * SHOWS and what the engine STORES. The prototype's definitions described only the
 * first half, and two of the mismatches fail silently (plan §4.2.1a).
 *
 * Paired with `rule-adapters.ts` (stored → drawer) and `rule-writer.ts` (drawer →
 * stored); all three share the tables below.
 */
import { CriteriaModifierType } from 'api-models'

export type CriterionKind =
	| 'boolean'
	| 'choice'
	| 'text'
	| 'number'
	/** Typed like `text`, but every Enter banks the word, so the criterion holds a LIST. */
	| 'keywords'
	/** Picked from the country menu, not typed. Values are country NAMES. */
	| 'country'

/** One selectable value: the word on screen, and the token the engine keeps. */
export interface CriterionOption {
	display: string
	stored: string
}

export interface CriterionDefinition {
	id: string
	label: string
	kind: CriterionKind
	/** The modifier this row reads from and writes to. */
	modifier: CriteriaModifierType
	/**
	 * Conversion only, and a READ-side alias rather than a second stored type: nothing
	 * is ever stored as `NOT_CONVERSION`. The engine handles only `CONVERSION` and reads
	 * `conversion.negative` off it.
	 */
	readAliasModifier?: CriteriaModifierType
	/** Operator pair the criterion toggles between; booleans have none. */
	operators?: ['is', 'is not'] | ['more than', 'less than']
	/** Display ↔ stored for a `choice` row. `options` is derived from it. */
	optionValues?: CriterionOption[]
	/** The words the drawer offers, in order. */
	options?: string[]
	placeholder?: string
	unit?: string
	hint?: string
	tier?: 'Standard' | 'Pro'
	/** Wording for criteria the product spells out rather than composing. */
	statements?: { positive: string; negative: string }
	/** The value refines the statement instead of being required by it. */
	optionalValue?: boolean
	/** Joins the statement to its value, e.g. "was recorded with label sale". */
	valuePrefix?: string
	/** How many values the ENGINE can hold, where that is fewer than the control offers.
	 *  Only `keyword` sets it — see the note on that definition below. */
	maxStoredValues?: number
	/** Why this criterion cannot carry a value on `is not`, and what to tell the user.
	 *  The ENGINE returns on `label` before it reads `negative`, so a negated criterion
	 *  with a value compiles to the POSITIVE condition — the exact inverse of the ask. */
	negatedValueProblem?: string
	/** A trigger source that makes this criterion unsatisfiable, and the reason — the
	 *  prototype had none, so it let you save a rule that could never fire. */
	unavailableWhenSource?: string
	unavailableReason?: string
}

/** Display words → stored tokens; lowercase is the default, exceptions pass both. */
const choiceValues = (
	display: string[],
	stored?: string[]
): CriterionOption[] =>
	display.map((word, index) => ({
		display: word,
		stored: stored ? stored[index] : word.toLowerCase()
	}))

/** IP usage is the one row whose words are nothing like its tokens. */
const IP_USAGE_VALUES = choiceValues(
	['Data center', 'Business', 'Residential'],
	['hosting', 'corporate', 'consumer']
)

const define = (definition: CriterionDefinition): CriterionDefinition => ({
	...definition,
	options:
		definition.options ??
		definition.optionValues?.map((option) => option.display)
})

export const CRITERIA_DEFINITIONS: CriterionDefinition[] = [
	define({
		id: 'bounced',
		label: 'Click has bounced',
		kind: 'boolean',
		modifier: CriteriaModifierType.NO_DEVICE,
		// A bounce is measured on the landing page, which a Device-keyed rule never sees.
		unavailableWhenSource: 'Device',
		unavailableReason:
			'Not available when the trigger counts clicks from the same Device. Pick Visitor instead.'
	}),
	define({
		id: 'bad-clicks',
		label: 'Click was previously detected as bad',
		kind: 'boolean',
		modifier: CriteriaModifierType.BAD_CLICKS
	}),
	define({
		id: 'proxy',
		label: 'Detected as VPN or other proxy',
		kind: 'boolean',
		modifier: CriteriaModifierType.PROXY
	}),
	define({
		id: 'threat-level',
		label: 'Detected threat level',
		kind: 'choice',
		modifier: CriteriaModifierType.THREAT_LEVEL,
		operators: ['is', 'is not'],
		optionValues: choiceValues(['Low', 'Medium', 'High'])
	}),
	define({
		id: 'ip-type',
		label: 'Detected IP address type',
		kind: 'choice',
		modifier: CriteriaModifierType.IP_USAGE,
		operators: ['is', 'is not'],
		optionValues: IP_USAGE_VALUES
	}),
	define({
		id: 'device-type',
		label: 'Detected device type',
		kind: 'choice',
		modifier: CriteriaModifierType.DEVICE_TYPE,
		operators: ['is', 'is not'],
		optionValues: choiceValues(['Mobile', 'Tablet', 'Computer'])
	}),
	define({
		id: 'country',
		label: 'Source country',
		kind: 'country',
		modifier: CriteriaModifierType.COUNTRY,
		operators: ['is', 'is not'],
		// Values here are country NAMES and the engine matches ISO alpha-2 CODES, so this
		// row translates through `config/countries`. An unmapped `is not` serialises to
		// `$nin` of names, which matches every click (plan §4.2.1a).
		placeholder: 'Any country'
	}),
	define({
		id: 'time-on-site',
		label: 'Time spent on website',
		kind: 'number',
		modifier: CriteriaModifierType.TIME,
		operators: ['more than', 'less than'],
		unit: 'seconds'
	}),
	define({
		id: 'page-views',
		label: 'Number of page views',
		kind: 'number',
		modifier: CriteriaModifierType.PAGE_VIEWS,
		operators: ['more than', 'less than']
	}),
	define({
		id: 'bot-probability',
		label: 'Detected bot probability level',
		kind: 'choice',
		modifier: CriteriaModifierType.BOT_PROBABILITY,
		operators: ['is', 'is not'],
		optionValues: choiceValues(['Low', 'Medium', 'High']),
		tier: 'Standard'
	}),
	define({
		id: 'interaction',
		label: 'Website interaction',
		kind: 'choice',
		modifier: CriteriaModifierType.INTERACTION,
		operators: ['is', 'is not'],
		// OPEN (plan §6 Q4). The prototype's None / Scrolled / Clicked / Submitted are
		// different SIGNALS, not a rewording of the levels `INTERACTION` stores, and
		// nothing in the engine produces them, so the stored levels ship.
		optionValues: choiceValues(['None', 'Low', 'Medium', 'High']),
		// Ungated on purpose: master carries `feature_tier: 'standard'`, but the ticket
		// lists this among the plain criteria. Removing a gate is a monetization change.
		tier: undefined
	}),
	define({
		id: 'conversion',
		label: 'Conversion',
		kind: 'text',
		modifier: CriteriaModifierType.CONVERSION,
		readAliasModifier: CriteriaModifierType.NOT_CONVERSION,
		operators: ['is', 'is not'],
		statements: { positive: 'was recorded', negative: 'was not recorded' },
		optionalValue: true,
		valuePrefix: 'with label',
		placeholder: 'Any conversion',
		hint: 'Leave the label blank to match any conversion',
		negatedValueProblem:
			'Remove the label, or switch to "is" — a label can only be matched on a conversion that WAS recorded.',
		tier: 'Pro'
	}),
	define({
		id: 'keyword',
		label: 'Search keyword',
		// The ENGINE holds one keyword: `$eq` / `$ne` against a scalar, so a saved list
		// matches nothing on `is` and EVERY click on `is not`. `maxStoredValues` records
		// that ceiling. OPEN (plan §4.2.1a): ship single-value, or move the engine to
		// `$in` / `$nin` first.
		kind: 'keywords',
		modifier: CriteriaModifierType.SEARCH_KEYWORD,
		operators: ['is', 'is not'],
		maxStoredValues: 1,
		placeholder: 'e.g. click fraud',
		hint: 'Supports only Google Ads clicks. One keyword per rule.'
	})
]

const BY_ID = new Map(
	CRITERIA_DEFINITIONS.map((definition) => [definition.id, definition])
)

export const criterionDefinition = (
	id: string
): CriterionDefinition | undefined => BY_ID.get(id)

/** A drawer word → the token the engine keeps; free text falls through unchanged. */
export const storedCriterionValue = (id: string, display: string): string =>
	BY_ID.get(id)?.optionValues?.find((option) => option.display === display)
		?.stored ?? display

/** A stored token → the word the drawer shows; unknown tokens pass through. */
export const displayCriterionValue = (id: string, stored: string): string => {
	const match = BY_ID.get(id)?.optionValues?.find(
		(option) => option.stored === stored
	)
	if (match) return match.display
	return stored ? stored.charAt(0).toUpperCase() + stored.slice(1) : stored
}

/**
 * Whether the workspace's plan covers a criterion's tier.
 *
 * The old page's gate is `accountTier === 'standard' && feature_tier === 'pro'`, but
 * `getPermissionTier` also resolves **lite**, where every gated row today is ungated.
 * Ordering the tiers closes that gap: on lite plans, rows that are pickable today
 * stop being pickable — worth saying in the PR. An unknown tier gates everything.
 */
const TIER_ORDER: Record<string, number> = {
	lite: 1,
	standard: 2,
	legacy: 2,
	pro: 3,
	custom: 3
}

export const isCriterionGated = (
	definition: CriterionDefinition,
	accountTier: string | null,
	/** Trials carry every feature, the same exemption `hasFeature` makes. */
	isTrial = false
): boolean => {
	if (!definition.tier) return false
	if (isTrial) return false
	const required = TIER_ORDER[definition.tier.toLowerCase()] ?? 3
	const held = accountTier ? TIER_ORDER[accountTier] ?? 0 : 0
	return held < required
}
